<template>
    <OrderCertificateTable>
        <template #actions>
            <DeleteBulkButton
                v-if="selected.length > 0"
                :selected="selected"
                @click.stop="bulkDeleteModalOpen = true"
            />
            <CreateButton
                title="create"
                :disabled="!platform.certificate_active"
                @click="createOrderCertificateModalOpen = true"
            />
        </template>
    </OrderCertificateTable>
    <DeleteItemsModal
        v-if="bulkDeleteModalOpen"
        id="delete-assets-modal"
        ref="deleteAssetsModal"
        v-model="certificatesToDelete"
        v-model:open="bulkDeleteModalOpen"
        title="modal_title_delete_certificates"
        id-key="uuid"
        name-key="name"
        @submit="bulkDeleteCertificates"
        @cancel="bulkDeleteModalOpen = false"
    />
    <AddOrderCertificateModal
        v-if="createOrderCertificateModalOpen"
        v-model:open="createOrderCertificateModalOpen"
        :model-value="platform"
        @cancel="createOrderCertificateModalOpen = false"
        @submit="createOrderCertificate"
    />
</template>

<script setup lang="ts">
import DeleteItemsModal from '@src/components/DeleteItemsModal.vue'
import { useTable } from '@src/composables/common'
import { Platform, PlatformCertificateDocument } from '@src/types'
import {
    deletePlatformOrderCertificates,
    getPlatformOrderCertificates,
    storePlatformOrderCertificate,
    updatePlatform,
} from '@src/utils/api/platforms'
import { useToastStore } from '@src/utils/stores/toast'
import moment from 'moment'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import CreateButton from '../actions/CreateButton.vue'
import DeleteBulkButton from '../actions/DeleteBulkButton.vue'
import AddOrderCertificateModal from './AddOrderCertificateModal.vue'

const props = defineProps<{
    platform: Platform
}>()

const orderCertificates = ref<PlatformCertificateDocument[]>([])
const platform = ref(props.platform)
const createOrderCertificateModalOpen = ref(false)
const bulkDeleteModalOpen = ref(false)
const certificatesToDelete = computed(() => {
    return selected.value.map((id) =>
        orderCertificates.value.find(
            (certificate: PlatformCertificateDocument) =>
                certificate.uuid === id
        )
    )
})

const router = useRouter()
const route = useRoute()
const i18n = useI18n()
const toast = useToastStore()

const getOrderCertificates = async () => {
    const response = await getPlatformOrderCertificates(props.platform)
    if (response.data.success) {
        orderCertificates.value = response.data.certificates
    }
}

const { TableWrapper: OrderCertificateTable, selected } = useTable({
    loadData: getOrderCertificates,
    filters: {
        name: '',
    },
    disableFilters: [],
    tableProps: {
        id: 'order-certificates-table',
        label: 'order_certificate',
        pagination: true,
        data: orderCertificates,
        total: orderCertificates.value.length,
        columns: [
            {
                label: 'ID',
                key: 'uuid',
                select: true,
            },
            {
                label: 'ID',
                key: 'uuid',
            },
            {
                label: 'Name',
                key: 'name',
            },
            {
                label: 'Language',
                key: 'lang',
                cellRenderer: (props: {
                    rowData: PlatformCertificateDocument
                }) => {
                    return props.rowData.lang === 'en' ? 'English' : 'German'
                },
            },
            {
                label: 'created_at',
                key: 'created_at',
                cellRenderer: (props: { rowData }) => {
                    return moment(props.rowData.created_on).format('DD.MM.yyyy')
                },
            },
        ],
    },
})

onMounted(async () => {
    await getOrderCertificates()
})

watch(
    () => platform.value.certificate_active,
    async () => {
        delete platform.value.certificate_document
        await updatePlatform(platform.value)
    }
)

const createOrderCertificate = async (
    certificateDocument: PlatformCertificateDocument
) => {
    const response = await storePlatformOrderCertificate(
        platform.value,
        certificateDocument
    )
    if (response.data.success) {
        orderCertificates.value = response.data.certificates
        createOrderCertificateModalOpen.value = false
        toast.success(i18n.t('certificate_uploaded'))
    }
}

const bulkDeleteCertificates = async () => {
    if (certificatesToDelete.value.length > 0) {
        const response = await deletePlatformOrderCertificates(
            platform.value,
            certificatesToDelete.value.map(
                (certificate) => certificate?.uuid as string
            )
        )
        if (response.data.success) {
            selected.value = []
            const newQuery = { ...route.query }
            delete newQuery['order-certificates-table_selected']
            router.replace({ query: newQuery })
            bulkDeleteModalOpen.value = false
            toast.success(i18n.t('certificates_deleted'))
            orderCertificates.value = response.data.certificates
        }
    }
}
</script>
